'use client';

/**
 * GuestV2MenuList — the Chow mobile menu body, on the web.
 *
 * The V1 `PlaceMenuList` is deliberately a platform menu with the merchant's
 * accent budgeted down to the add control. This is the other half of the same
 * idea: the list the Chow app draws when a guest asks for the simple view, or
 * when a branch published no design. Same data as `PlaceMenuList`; different
 * composition.
 *
 *   rows      ┌──────────────┬──────┐  One dense scan. 96px photo, 16px name,
 *             │ Name         │ img  │  13px description, 15px accent price.
 *             │ Description  │  (+) │
 *             │ ₵00.00       │      │
 *             └──────────────┴──────┘
 *
 *   cards     ┌────────┐ ┌────────┐  Two columns, circular plate. The picture
 *             │ (img)  │ │ (img)  │  sells, so the title is short and the
 *             │ Name   │ │ Name   │  description is not shown.
 *             │ ₵00 (+)│ │ ₵00 (+)│
 *             └────────┘ └────────┘
 *
 *   showcase  ┌──────────────────────┐  One dish per row, photo first, padded
 *             │        image         │  body. For a short menu where each dish
 *             │ Name        ₵00.00   │  is the reason someone came.
 *             │ Description     (+)  │
 *             └──────────────────────┘
 *
 * DIETARY FILTERING IS THE GUEST'S SWITCH, NOT A HIDDEN RULE. Dishes that clash
 * are flagged by default (`describeItemDietary`), and hiding them happens only
 * when the guest presses the filter pill in `GuestV2MenuNav`. The same rule the
 * Chow app follows (blueprint R13): flag, never silently hide.
 *
 * ORDERING NEVER DEPENDS ON THIS FILE. The ordering island captures item taps
 * at the document level, so the row's open/add handlers are the only contract.
 *
 * @location components/place/GuestV2MenuList.tsx
 */

import Image from 'next/image';
import { AlertTriangle, Coffee, Info, Plus, Search } from 'lucide-react';
import type { CSSProperties } from 'react';

import { cn } from '@/components/ui/utils';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';
import { describeItemDietary, type DietaryProfile } from '@/lib/menu/dietary';
import {
  formatMenuPrice,
  type PlaceMenuLayout,
  type PlaceMenuSection,
} from '@/components/place/PlaceMenuList';

type ListProps = {
  sections: PlaceMenuSection[];
  layout?: PlaceMenuLayout;
  onOpenItem: (item: OrderingMenuItem) => void;
  /** Omitted when the branch cannot take an order — no add control is drawn. */
  onAddItem?: (item: OrderingMenuItem) => void;
  /** The device-local dietary profile; empty means "say nothing". */
  dietaryProfile: DietaryProfile;
  /** Set by the guest's filter pill. */
  hideDietaryClashes?: boolean;
  /** Used for the honest empty state, and for "Clear search". */
  searchQuery?: string;
  onClearSearch?: () => void;
  emptyMessage?: string;
  /** The merchant's display face, when the template supplies one. */
  headingFont?: string;
  className?: string;
};

export function GuestV2MenuList({
  sections,
  layout = 'rows',
  onOpenItem,
  onAddItem,
  dietaryProfile,
  hideDietaryClashes = false,
  searchQuery = '',
  onClearSearch,
  emptyMessage = 'Nothing on the menu right now.',
  headingFont,
  className,
}: ListProps) {
  const populated = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!hideDietaryClashes) return true;
        return !describeItemDietary(item, dietaryProfile).clash;
      }),
    }))
    .filter((section) => section.items.length > 0);

  if (populated.length === 0) {
    const searching = searchQuery.trim().length > 0;
    return (
      <div className={cn('gv-container gv-menu', className)}>
        <div className="gv-menu-empty">
          <span className="gv-menu-empty__icon">
            <Search aria-hidden="true" className="size-5" />
          </span>
          <p className="gv-menu-empty__title">{searching ? 'No dishes match' : emptyMessage}</p>
          {searching ? (
            <p className="gv-menu-empty__body">
              Nothing on this menu matches &ldquo;{searchQuery.trim()}&rdquo;.
            </p>
          ) : null}
          {searching && onClearSearch ? (
            <button type="button" className="gv-menu-empty__action" onClick={onClearSearch}>
              Clear search
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const headingStyle: CSSProperties | undefined = headingFont ? { fontFamily: headingFont } : undefined;
  const showHeadings = populated.length > 1;

  return (
    <div className={cn('gv-container gv-menu', className)}>
      <div className="gv-menu__sections">
        {populated.map((section) => (
          <section key={section.id} className="gv-menu__section">
            {showHeadings ? (
              <div className="gv-menu__section-head">
                <h2 className="gv-menu__heading" style={headingStyle}>
                  {section.name}
                </h2>
                {section.description ? (
                  <p className="gv-menu__section-note">{section.description}</p>
                ) : null}
              </div>
            ) : null}

            <ul className={cn('gv-menu-grid', `gv-menu-grid--${layout}`)}>
              {section.items.map((item) => {
                const shared = {
                  item,
                  dietaryProfile,
                  onOpen: () => onOpenItem(item),
                  onAdd: onAddItem ? () => onAddItem(item) : undefined,
                  headingStyle,
                };
                if (layout === 'cards') return <GuestV2MenuCard key={item.id} {...shared} />;
                if (layout === 'showcase') return <GuestV2MenuShowcase key={item.id} {...shared} />;
                return <GuestV2MenuRow key={item.id} {...shared} />;
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

type ItemProps = {
  item: OrderingMenuItem;
  dietaryProfile: DietaryProfile;
  onOpen: () => void;
  onAdd?: () => void;
  headingStyle?: CSSProperties;
};

function itemName(item: OrderingMenuItem): string {
  return item.title || item.name || 'Item';
}

function isOrderable(item: OrderingMenuItem): boolean {
  // `available_for_order` is the branch-level verdict and outranks the item's
  // own flag: an item can be on the menu and still be off at this branch today.
  return item.available !== false && item.available_for_order !== false;
}

function unavailableLabel(item: OrderingMenuItem): string {
  return item.availability_label || 'Sold out';
}

function DietNote({ note, clash }: { note: string; clash: boolean }) {
  const Icon = clash ? AlertTriangle : Info;
  return (
    <span className={cn('gv-menu-diet-note', clash && 'gv-menu-diet-note--clash')}>
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className="truncate">{note}</span>
    </span>
  );
}

function AddButton({
  name,
  onAdd,
  size = 'md',
}: {
  name: string;
  onAdd: () => void;
  size?: 'md' | 'card';
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onAdd();
      }}
      aria-label={`Add ${name} to your order`}
      className={cn('gv-menu-add', size === 'card' && 'gv-menu-add--card')}
    >
      <span>
        <Plus aria-hidden="true" className="size-[18px]" />
      </span>
    </button>
  );
}

function GuestV2MenuRow({ item, dietaryProfile, onOpen, onAdd, headingStyle }: ItemProps) {
  const name = itemName(item);
  const price = formatMenuPrice(item);
  const orderable = isOrderable(item);
  const verdict = describeItemDietary(item, dietaryProfile);

  return (
    <li className={cn('gv-menu-row', !orderable && 'is-unavailable')}>
      <button type="button" onClick={onOpen} className="gv-menu-row__open">
        <span className="gv-menu-row__text">
          <span className="gv-menu-row__title" style={headingStyle}>
            {name}
          </span>
          {verdict.note ? <DietNote note={verdict.note} clash={verdict.clash} /> : null}
          {item.description ? <span className="gv-menu-row__desc">{item.description}</span> : null}
          <span className="gv-menu-row__price-row">
            <span className={cn('gv-menu-row__price', !orderable && 'is-muted')}>
              {orderable ? price : unavailableLabel(item)}
            </span>
          </span>
        </span>

        <span className={cn('gv-menu-row__media', item.image ? 'has-image' : null)}>
          {item.image ? (
            <span className="gv-menu-row__photo">
              <Image src={item.image} alt="" fill sizes="96px" className="object-cover" loading="lazy" />
            </span>
          ) : null}
        </span>
      </button>
      {onAdd && orderable ? <AddButton name={name} onAdd={onAdd} /> : null}
    </li>
  );
}

function GuestV2MenuCard({ item, dietaryProfile, onOpen, onAdd, headingStyle }: ItemProps) {
  const name = itemName(item);
  const price = formatMenuPrice(item);
  const orderable = isOrderable(item);
  const verdict = describeItemDietary(item, dietaryProfile);

  return (
    <li className={cn('gv-menu-card', !orderable && 'is-unavailable')}>
      <button type="button" onClick={onOpen} className="gv-menu-card__open">
        <span className="gv-menu-card__plate">
          {item.image ? (
            <Image
              src={item.image}
              alt=""
              fill
              sizes="(max-width: 700px) 40vw, 200px"
              className="object-cover"
              loading="lazy"
            />
          ) : (
            <Coffee aria-hidden="true" className="size-7 opacity-45" />
          )}
        </span>

        <span className="gv-menu-card__title" style={headingStyle}>
          {name}
        </span>
        {verdict.note ? <DietNote note={verdict.note} clash={verdict.clash} /> : null}
      </button>

      <div className="gv-menu-card__foot">
        <span className={cn('gv-menu-card__price', !orderable && 'is-muted')}>
          {orderable ? price : unavailableLabel(item)}
        </span>
        {onAdd && orderable ? <AddButton name={name} onAdd={onAdd} size="card" /> : null}
      </div>
    </li>
  );
}

function GuestV2MenuShowcase({ item, dietaryProfile, onOpen, onAdd, headingStyle }: ItemProps) {
  const name = itemName(item);
  const price = formatMenuPrice(item);
  const orderable = isOrderable(item);
  const verdict = describeItemDietary(item, dietaryProfile);

  return (
    <li className={cn('gv-menu-showcase', !orderable && 'is-unavailable')}>
      <button type="button" onClick={onOpen} className="gv-menu-showcase__open">
        <span className="gv-menu-showcase__photo">
          {item.image ? (
            <Image
              src={item.image}
              alt=""
              fill
              sizes="(max-width: 700px) 100vw, 600px"
              className="object-cover"
              loading="lazy"
            />
          ) : (
            <Coffee aria-hidden="true" className="size-8 opacity-45" />
          )}
        </span>
        <span className="gv-menu-showcase__body">
          <span className="gv-menu-showcase__title" style={headingStyle}>
            {name}
          </span>
          {verdict.note ? <DietNote note={verdict.note} clash={verdict.clash} /> : null}
          {item.description ? <span className="gv-menu-showcase__desc">{item.description}</span> : null}
        </span>
      </button>

      <div className="gv-menu-showcase__foot">
        <span className={cn('gv-menu-showcase__price', !orderable && 'is-muted')}>
          {orderable ? price : unavailableLabel(item)}
        </span>
        {onAdd && orderable ? <AddButton name={name} onAdd={onAdd} size="card" /> : null}
      </div>
    </li>
  );
}
